import { useState } from 'react'
import { CheckCircle, MessageCircle } from 'lucide-react'
import { useAuthStore } from '@/stores/authStore'
import { useUserStore } from '@/stores/userStore'
import { toast } from '@/hooks/use-toast'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogClose,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Card, CardContent } from '@/components/ui/card'
import { requestEnterprisePlanUpgrade } from '@/api/billing'
import { useTranslation } from 'react-i18next'
import { tokens } from '@/locales/translations/tokens'

interface PlanFormEmailProps {
  className?: string
  externalOpen?: boolean
  setExternalOpen?: (open: boolean) => void
  triggerButton?: React.ReactNode
}

const PlanFormEmail = ({
  className,
  externalOpen,
  setExternalOpen,
  triggerButton,
}: PlanFormEmailProps) => {
  const { t } = useTranslation();
  const [message, setMessage] = useState('')
  const [subject] = useState(
    'Alan Workstation - ' + t(tokens.onboarding.organization_pricing.contact_sales_modal.request_to_upgrade_placeholder)
  )
  const [internalOpen, setInternalOpen] = useState(false)
  const [submitted, setSubmitted] = useState(false)
  const authUser = useAuthStore((state) => state.auth.user)
  const userData = useUserStore((state) => state.user.userData)

  const isOpen = externalOpen !== undefined ? externalOpen : internalOpen
  const setIsOpen = setExternalOpen || setInternalOpen

  const handleSubmit = async () => {
    try {
      await requestEnterprisePlanUpgrade({
        user_message: message,
      })
      toast({
        title: t(tokens.ui.toasts.request.send_success.title),
        description: t(tokens.ui.toasts.request.review),
      })
      setSubmitted(true)
    } catch (_error) {
      toast({
        title: t(tokens.ui.toasts.error.title),
        description: t(tokens.ui.toasts.request.failed),
      })
    }
  }

  const handleClose = () => {
    setIsOpen(false)
    setMessage('')
    setSubmitted(false)
    
    window.location.replace('/')
  }

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      {!externalOpen && (
        <DialogTrigger asChild>
          {triggerButton || (
            <Button className={className}> <MessageCircle /> {t(tokens.onboarding.organization_pricing.contact_sales)}</Button>
          )}
        </DialogTrigger>
      )}
      <DialogContent className='sm:max-w-[425px]'>
        {!submitted ? (
          <>
            <DialogHeader>
              <DialogTitle>{t(tokens.onboarding.organization_pricing.contact_sales_modal.upgrade_to_enterprise)}</DialogTitle>
              <DialogDescription>
                {t(tokens.onboarding.organization_pricing.contact_sales_modal.submit_request)}
              </DialogDescription>
            </DialogHeader>
            <div className='grid gap-4 py-4'>
              <div className='grid gap-2'>
                <Label htmlFor='email'>{t(tokens.onboarding.organization_pricing.contact_sales_modal.my_email)}</Label>
                <Input
                  id='email'
                  value={userData?.email || authUser?.email || 'Loading...'}
                  disabled={true}
                  placeholder='email@email.com'
                />
              </div>
              <div className='grid gap-2'>
                <Label htmlFor='subject'>{t(tokens.onboarding.organization_pricing.contact_sales_modal.subject)}</Label>
                <Input
                  id='subject'
                  value={subject}
                  disabled={true}
                  placeholder={subject}
                />
              </div>
              <div className='grid gap-2'>
                <Label htmlFor='message'>{t(tokens.onboarding.organization_pricing.contact_sales_modal.additional_message)}</Label>
                <Textarea
                  id='message'
                  placeholder={t(tokens.onboarding.organization_pricing.contact_sales_modal.describe_needs)}
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  className='resize-none'
                  rows={4}
                />
              </div>
            </div>
            <DialogFooter>
              <DialogClose asChild>
                <Button variant='outline'>{t(tokens.ui.actions.cancel)}</Button>
              </DialogClose>
              <Button onClick={handleSubmit}>{t(tokens.onboarding.organization_pricing.contact_sales_modal.send_request)}</Button>
            </DialogFooter>
          </>
        ) : (
          <div className='flex flex-col items-center space-y-6 py-6'>
            <CheckCircle className='h-16 w-16 text-green-500' />
            <div className='text-center'>
              <h3 className='mb-2 text-xl font-semibold'>{t(tokens.onboarding.organization_pricing.contact_sales_modal.request_submitted)}</h3>
              <p className='mb-6 text-muted-foreground'>
                {t(tokens.onboarding.organization_pricing.contact_sales_modal.sales_team_contact)}
              </p>
            </div>

            <Card className="w-full border border-muted">
              <CardContent className="p-4">
                <div className="flex flex-col space-y-2">
                  <h4 className="font-medium">{t(tokens.onboarding.organization_pricing.contact_sales_modal.whats_next)}</h4>
                  <p className="text-sm text-muted-foreground">
                    {t(tokens.onboarding.organization_pricing.contact_sales_modal.continue_with_community_plan)}
                  </p>
                </div>
              </CardContent>
            </Card>

            <Button onClick={handleClose} className='mt-4 w-full'>
              {t(tokens.onboarding.organization_pricing.contact_sales_modal.continue_with_current_plan)}
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  )
}

export default PlanFormEmail
